package com.elan.edubridge.ui.staff

import com.elan.edubridge.shared.di.SharedGraph
import com.elan.edubridge.shared.domain.model.AttendanceSource
import com.elan.edubridge.shared.domain.model.CommandFailure
import com.elan.edubridge.shared.domain.model.QrSession
import com.elan.edubridge.shared.domain.model.QrSessionStatus
import com.elan.edubridge.shared.domain.model.QrToken
import com.elan.edubridge.shared.domain.model.SessionState
import com.elan.edubridge.ui.shell.Router
import com.elan.edubridge.ui.shell.escapeHtml
import com.elan.edubridge.ui.shell.icon
import com.elan.edubridge.ui.shell.renderPage
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.Job
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeoutOrNull
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.svg.SVGElement
import kotlin.js.Date
import kotlin.js.jsTypeOf

/**
 * 출결 QR — 관리 화면(§3.5.1) + 키오스크 전체화면(§3.5.2). 계획 §5.4 요건 6종을 전부 구현한다:
 * 전체화면(Fullscreen API) · Wake Lock(+미지원 안내) · 320px 이상 고대비 QR · 회전 카운트다운 링 ·
 * 네트워크 단절 시 QR 숨김 · 재인증 UI. 최근 스캔은 이름 마스킹(§3.5.3).
 *
 * Fullscreen·Wake Lock은 Kotlin stdlib의 org.w3c.dom 바인딩이 아직 다루지 않는(또는 버전에 따라
 * 다른) 실험적/최신 브라우저 API라 `dynamic`으로 직접 접근하고 기능 감지로 미지원을 흡수한다.
 *
 * **리뷰 후속(B5·M16·M17·M18·M19)**: 최근 스캔 조회는 `Flow.first()`(3s 타임아웃)로 스냅샷 1건만
 * 가져오고 바로 구독을 끊는다 — Firebase `callbackFlow`는 완료되지 않으므로 예전처럼 `.collect{}`로
 * 기다리면 실 서비스에서 영원히 매달린다(InMemory만 1회 방출 후 끝나 우연히 동작했다). 회전 실패는
 * 지수 백오프(1s→최대 60s)하고, 세션이 끝났다고 확정되면(NOT_FOUND/세션 ENDED) 루프를 끊고 재시작
 * 안내로 전환한다. 라우터 이탈 시 인터벌·WakeLock·풀스크린을 정리하도록 `Router.setCleanup`에
 * 등록한다. `beforeunload`에서는 best-effort로 `endQrSession`을 보낸다(§M19).
 */
object QrKioskPage {
    private val scope = MainScope()
    private val commandRepository = SharedGraph.createAcademyCommandRepository()
    private val observeAttendanceFeed = SharedGraph.createObserveAttendanceFeedUseCase()

    private var activeSession: QrSession? = null
    private var activeToken: QrToken? = null
    private var rotationIntervalId: Int? = null
    private var clockIntervalId: Int? = null
    private var secondsRemaining = 0
    private var wakeLockSentinel: dynamic = null
    private var onlineHandlerWired = false
    private var lifecycleHandlersWired = false

    // 회전 실패 백오프 상태(M16).
    private var isBackingOff = false
    private var backoffSecondsRemaining = 0
    private var retryFailureCount = 0

    private var activeJob: Job? = null
    private var rotationJob: Job? = null
    private var routeGeneration = 0

    // ==================== 관리 화면(§3.5.1) ====================

    fun openManagement(session: SessionState, academyId: String) {
        val generation = beginRoute()
        ensureLifecycleHandlersWired()
        Router.setCleanup {
            cleanupRoute(generation)
        }
        activeJob = scope.launch {
            if (activeSession?.academyId != null && activeSession?.academyId != academyId) {
                activeSession = null
                activeToken = null
            }
            if (isCurrentRoute(generation)) renderManagement(session, academyId, generation)
        }
    }

    private suspend fun renderManagement(session: SessionState, academyId: String, generation: Int) {
        val recent = recentScans(academyId)
        if (!isCurrentRoute(generation)) return
        val html = managementContentHtml(academyId, recent)
        DashboardShell.render(session, academyId, "attendance-qr", html) {
            wireManagement(session, academyId, generation)
        }
        val preview = document.getElementById("qr-panel-preview") as? HTMLElement
        val token = activeToken
        val currentSession = activeSession
        if (preview != null && token != null && currentSession != null && currentSession.status == QrSessionStatus.ACTIVE) {
            renderQrToken(academyId, currentSession.sessionId, token)
            if (!isCurrentRoute(generation)) return
            resetBackoff()
            startRotation(academyId, currentSession.sessionId, generation)
        }
    }

    private fun managementContentHtml(academyId: String, recent: List<MaskedScan>): String {
        val session = activeSession
        val createPanel = """
            <section class="eb-card">
                <h2>새 출결 QR</h2>
                <p class="eb-meta">QR을 띄울 위치를 알아보기 쉬운 이름으로 입력해 주세요.</p>
                <div class="eb-field">
                    <label for="qr-session-label">세션 이름</label>
                    <input id="qr-session-label" maxlength="60" required placeholder="예: 1층 로비" aria-describedby="qr-session-label-help">
                    <p id="qr-session-label-help" class="eb-meta">1~60자로 입력해 주세요.</p>
                </div>
                <button class="eb-button eb-button--primary" id="create-session">QR 만들기</button>
            </section>
        """.trimIndent()
        val panel = if (session == null || session.status == QrSessionStatus.ENDED) {
            """
            <section class="eb-card">
                <div class="eb-empty-state">
                    ${icon("icon-qr", "xl")}
                    <h2>선택한 QR 세션이 없어요</h2>
                    <p>위에서 세션 이름을 입력하고 QR을 만들어 주세요.</p>
                </div>
            </section>
            """.trimIndent()
        } else {
            """
            <section class="eb-card eb-qr-panel">
                <div class="eb-qr-panel__preview">
                    <div class="eb-qr-code" id="qr-panel-preview" role="img" aria-label="출결 QR 코드, ${session.rotationSeconds}초마다 갱신"></div>
                    <p class="eb-qr-panel__rotate">${icon("icon-refresh", "xs", "eb-spin")} <span id="qr-panel-countdown">${session.rotationSeconds}초 후 갱신</span></p>
                </div>
                <div class="eb-qr-panel__meta">
                    <dl>
                        <div><dt>세션</dt><dd>${session.label.escapeHtml()}</dd></div>
                        <div><dt>상태</dt><dd><span class="eb-chip eb-chip--success">사용 중</span></dd></div>
                    </dl>
                    <button class="eb-button eb-button--primary eb-button--lg" id="open-kiosk">
                        ${icon("icon-expand", "sm")} 키오스크 화면 열기
                    </button>
                    <button class="eb-button eb-button--ghost" id="end-session">세션 종료</button>
                </div>
            </section>
            """.trimIndent()
        }

        val scanItems = if (recent.isEmpty()) {
            """<li class="eb-meta">아직 스캔 기록이 없어요.</li>"""
        } else {
            recent.joinToString("") { """<li><time>${it.time.escapeHtml()}</time><span>${it.maskedName.escapeHtml()} 학생 · ${it.label}</span></li>""" }
        }

        return """
            <h1 class="eb-page-title">출결 QR</h1>
            <p class="eb-meta">태블릿에 QR을 띄워두면 학생이 스캔해 등원·하원을 처리해요. QR은 ${session?.rotationSeconds ?: 20}초마다 자동으로 바뀌어요.</p>
            $createPanel
            $panel
            <section class="eb-card">
                <h2>최근 스캔</h2>
                <ol class="eb-scan-list">$scanItems</ol>
            </section>
        """.trimIndent()
    }

    private fun wireManagement(session: SessionState, academyId: String, generation: Int) {
        (document.getElementById("create-session") as? HTMLButtonElement)?.onclick = {
            val label = (document.getElementById("qr-session-label") as? HTMLInputElement)?.value?.trim().orEmpty()
            if (label.isEmpty() || label.length > MAX_QR_SESSION_LABEL_LENGTH) {
                Toast.error("세션 이름을 1~60자로 입력해 주세요.")
            } else {
                scope.launch {
                    val result = commandRepository.createQrSession(academyId, label)
                    if (!isCurrentRoute(generation)) return@launch
                    val createdSession = result.session
                    val createdToken = result.token
                    if (result.isSuccess && createdSession != null && createdToken != null) {
                        activeSession = createdSession
                        activeToken = createdToken
                        renderManagement(session, academyId, generation)
                    } else {
                        Toast.error(result.errorMessage ?: "세션을 시작하지 못했어요.")
                    }
                }
            }
            null
        }
        (document.getElementById("end-session") as? HTMLButtonElement)?.onclick = {
            val current = activeSession
            if (current != null) {
                scope.launch {
                    commandRepository.endQrSession(academyId, current.sessionId)
                    if (!isCurrentRoute(generation)) return@launch
                    activeSession = current.copy(status = QrSessionStatus.ENDED)
                    activeToken = null
                    renderManagement(session, academyId, generation)
                }
            }
            null
        }
        (document.getElementById("open-kiosk") as? HTMLButtonElement)?.onclick = {
            Router.navigate("/app/$academyId/qr/kiosk")
            null
        }
    }

    // ==================== 키오스크 전체화면(§3.5.2) ====================

    fun openKiosk(session: SessionState, academyId: String) {
        val current = activeSession
        if (current == null || current.status != QrSessionStatus.ACTIVE) {
            Toast.error("먼저 관리 화면에서 QR 세션을 시작해 주세요.")
            Router.replace("/app/$academyId/attendance-qr")
            return
        }

        val generation = beginRoute()
        ensureLifecycleHandlersWired()
        renderPage("eb-kiosk", kioskHtml(session, current))
        wireKiosk(academyId)
        requestFullscreen()
        requestWakeLock()
        startClock()
        wireNetworkHandlers()
        resetBackoff()
        activeJob = scope.launch {
            activeToken?.let { token ->
                renderQrToken(academyId, current.sessionId, token)
            }
            if (isCurrentRoute(generation)) refreshRecentScans(academyId, generation)
        }
        startRotation(academyId, current.sessionId, generation)

        // 다른 라우트로 넘어가기 전(뒤로가기 포함) 반드시 정리되도록 등록 — exitKiosk()가 이미
        // 호출했더라도 stopRotation 등은 멱등이라 두 번 불려도 안전하다.
        Router.setCleanup { cleanupRoute(generation) }
    }

    private fun kioskHtml(session: SessionState, qrSession: QrSession): String = """
        <main class="eb-kiosk__stage">
            <header class="eb-kiosk__header">
                <span class="eb-kiosk__academy">${(session.activeMembership?.academyName ?: "").escapeHtml()}</span>
                <time class="eb-kiosk__clock" id="kiosk-clock"></time>
            </header>

            <div class="eb-kiosk__qr-wrap">
                <div class="eb-kiosk__qr-frame">
                    <svg class="eb-kiosk__ring" viewBox="0 0 100 100" aria-hidden="true">
                        <circle cx="50" cy="50" r="46" class="eb-kiosk__ring-track" />
                        <circle cx="50" cy="50" r="46" class="eb-kiosk__ring-progress" id="kiosk-ring-progress" />
                    </svg>
                    <div class="eb-qr-code--kiosk" id="kiosk-qr" role="img" aria-label="출결 QR 코드, ${qrSession.rotationSeconds}초마다 갱신"></div>
                </div>
                <p class="eb-kiosk__hint">QR을 카메라로 스캔해 주세요</p>
                <p class="eb-kiosk__wakelock-warning" id="wakelock-warning" hidden>
                    이 브라우저는 화면 자동 꺼짐 방지를 지원하지 않아요. 태블릿 디스플레이 설정에서 절전 모드를 꺼주세요.
                </p>
            </div>

            <ul class="eb-kiosk__recent" id="kiosk-recent" aria-live="polite" aria-label="최근 스캔 결과"></ul>

            <button class="eb-kiosk__exit" id="kiosk-exit" aria-label="키오스크 종료하고 관리 화면으로">${icon("icon-close", "sm")}</button>
        </main>

        <div class="eb-kiosk__overlay eb-kiosk__overlay--offline" id="offline-overlay" hidden>
            ${icon("icon-offline", "xl")}
            <h2>네트워크를 확인해 주세요</h2>
            <p>연결이 끊겨 QR을 갱신할 수 없어요. 선생님께 수동 출결 처리를 요청하세요.</p>
        </div>
        <div class="eb-kiosk__overlay eb-kiosk__overlay--reauth" id="reauth-overlay" hidden>
            <h2>다시 로그인해 주세요</h2>
            <p>세션이 만료됐어요. 이 화면은 태블릿 관리자만 재인증할 수 있어요.</p>
            <button class="eb-button eb-button--primary eb-button--lg" id="reauth-button">재로그인</button>
        </div>
        <div class="eb-kiosk__overlay eb-kiosk__overlay--session-ended" id="session-ended-overlay" hidden>
            ${icon("icon-qr", "xl")}
            <h2>세션이 종료되었습니다</h2>
            <p>이 QR 세션은 더 이상 유효하지 않아요. 관리 화면에서 새로 시작해 주세요.</p>
            <button class="eb-button eb-button--primary eb-button--lg" id="session-ended-restart">새로 시작</button>
        </div>
    """.trimIndent()

    private fun wireKiosk(academyId: String) {
        (document.getElementById("kiosk-exit") as? HTMLElement)?.onclick = {
            exitKiosk(academyId)
            null
        }
        (document.getElementById("reauth-button") as? HTMLElement)?.onclick = {
            Router.navigate("/login?redirect=/app/$academyId/qr/kiosk")
            null
        }
        (document.getElementById("session-ended-restart") as? HTMLElement)?.onclick = {
            exitKiosk(academyId)
            null
        }
    }

    private fun exitKiosk(academyId: String) {
        cleanupKiosk()
        Router.navigate("/app/$academyId/attendance-qr")
    }

    /** 인터벌·시계·WakeLock·전체화면을 전부 정리한다(M17·M18). 각 하위 함수는 멱등이라
     * [Router.setCleanup] 경로와 [exitKiosk]의 직접 호출이 겹쳐도 문제없다. */
    private fun cleanupKiosk() {
        stopRotation()
        stopClock()
        releaseWakeLock()
        exitFullscreenIfActive()
    }

    // ==================== 회전 · 카운트다운 · 백오프(§5.4, M16) ====================

    private fun resetBackoff() {
        isBackingOff = false
        backoffSecondsRemaining = 0
        retryFailureCount = 0
    }

    private fun startRotation(academyId: String, sessionId: String, generation: Int) {
        if (!isCurrentRoute(generation)) return
        stopRotation()
        secondsRemaining = activeToken?.rotationSeconds ?: activeSession?.rotationSeconds ?: 20
        drawRotationProgress()
        rotationIntervalId = window.setInterval({ tickRotation(academyId, sessionId, generation) }, 1000)
    }

    private fun tickRotation(academyId: String, sessionId: String, generation: Int) {
        if (!isCurrentRoute(generation)) return
        if (window.navigator.onLine == false) {
            showOffline(true)
            return
        }
        if (isBackingOff) {
            backoffSecondsRemaining -= 1
            if (backoffSecondsRemaining > 0) return
            isBackingOff = false
            attemptRotate(academyId, sessionId, generation)
            return
        }
        secondsRemaining -= 1
        if (secondsRemaining > 0) {
            drawRotationProgress()
            return
        }
        attemptRotate(academyId, sessionId, generation)
    }

    private fun attemptRotate(academyId: String, sessionId: String, generation: Int) {
        if (!isCurrentRoute(generation) || rotationJob?.isActive == true) return
        rotationJob = scope.launch {
            try {
                val result = commandRepository.rotateQrToken(academyId, sessionId)
                if (!isCurrentRoute(generation)) return@launch
                when {
                    result.failure == CommandFailure.UNAUTHENTICATED -> {
                        (document.getElementById("reauth-overlay") as? HTMLElement)?.removeAttribute("hidden")
                        stopRotation()
                    }
                    // NOT_FOUND(세션 문서 없음) · FAILED_PRECONDITION(세션이 ACTIVE가 아님, InMemory
                    // 스텁도 이 코드로 "종료된 세션" 실패를 표현한다) — 둘 다 재시도로 회복되지 않는다.
                    result.failure == CommandFailure.NOT_FOUND || result.failure == CommandFailure.FAILED_PRECONDITION -> {
                        stopRotation()
                        activeSession = activeSession?.copy(status = QrSessionStatus.ENDED)
                        activeToken = null
                        showSessionEnded(true)
                    }
                    !result.isSuccess || result.token == null -> {
                        retryFailureCount += 1
                        isBackingOff = true
                        backoffSecondsRemaining = backoffDelaySeconds()
                        showOffline(true)
                    }
                    else -> {
                        resetBackoff()
                        showOffline(false)
                        val token = result.token!!
                        activeToken = token
                        secondsRemaining = token.rotationSeconds
                        drawRotationProgress()
                        renderQrToken(academyId, sessionId, token)
                        if (isCurrentRoute(generation)) refreshRecentScans(academyId, generation)
                    }
                }
            } finally {
                if (isCurrentRoute(generation)) rotationJob = null
            }
        }
    }

    /** 1s → 2s → 4s → ... → 최대 60s 지수 백오프. */
    private fun backoffDelaySeconds(): Int = (1 shl retryFailureCount.coerceAtMost(6)).coerceAtMost(60)

    private fun showSessionEnded(ended: Boolean) {
        val overlay = document.getElementById("session-ended-overlay") as? HTMLElement
        if (ended) {
            overlay?.removeAttribute("hidden")
            setQrHidden(true)
        } else {
            overlay?.setAttribute("hidden", "")
        }
    }

    private fun drawRotationProgress() {
        (document.getElementById("qr-panel-countdown") as? HTMLElement)?.textContent = "${secondsRemaining}초 후 갱신"
        // <circle>은 SVGElement다 — HTMLElement로 캐스팅하면 항상 null이 된다(둘은 서로 다른 상속 갈래).
        val ring = document.getElementById("kiosk-ring-progress") as? SVGElement ?: return
        val rotationSeconds = (activeSession?.rotationSeconds ?: 20).coerceAtLeast(1)
        val circumference = 289.0
        val fraction = (rotationSeconds - secondsRemaining).toDouble() / rotationSeconds.toDouble()
        val offset = (circumference * fraction).coerceIn(0.0, circumference)
        ring.style.setProperty("stroke-dashoffset", "$offset")
    }

    private fun stopRotation() {
        rotationIntervalId?.let { window.clearInterval(it) }
        rotationIntervalId = null
    }

    private fun beginRoute(): Int {
        routeGeneration += 1
        activeJob?.cancel()
        activeJob = null
        rotationJob?.cancel()
        rotationJob = null
        cleanupKiosk()
        return routeGeneration
    }

    private fun cleanupRoute(generation: Int) {
        if (!isCurrentRoute(generation)) return
        routeGeneration += 1
        activeJob?.cancel()
        activeJob = null
        rotationJob?.cancel()
        rotationJob = null
        cleanupKiosk()
    }

    private fun isCurrentRoute(generation: Int): Boolean = routeGeneration == generation

    // ==================== 네트워크 단절(§5.4, §4.5) ====================

    private fun wireNetworkHandlers() {
        showOffline(window.navigator.onLine == false)
        if (onlineHandlerWired) return
        onlineHandlerWired = true
        window.addEventListener("offline", { showOffline(true) })
        window.addEventListener("online", { showOffline(false) })
    }

    /** 만료 QR을 계속 띄우지 않는다(§5.4 명시 요건) — 끊기면 QR 컨테이너를 숨기고 안내로 덮는다. */
    private fun showOffline(offline: Boolean) {
        val overlay = document.getElementById("offline-overlay") as? HTMLElement
        if (offline) {
            overlay?.removeAttribute("hidden")
            setQrHidden(true)
        } else {
            overlay?.setAttribute("hidden", "")
            setQrHidden(false)
        }
    }

    private suspend fun renderQrToken(academyId: String, sessionId: String, token: QrToken) {
        val payload = QrRenderer.payload(academyId, sessionId, token.nonce)
        (document.getElementById("kiosk-qr") as? HTMLElement)?.let {
            QrRenderer.renderInto(it, payload, pixelSize = 480)
        }
        (document.getElementById("qr-panel-preview") as? HTMLElement)?.let {
            QrRenderer.renderInto(it, payload, pixelSize = 200)
        }
    }

    private fun setQrHidden(hidden: Boolean) {
        listOf("kiosk-qr", "qr-panel-preview").forEach { id ->
            val qr = document.getElementById(id) as? HTMLElement ?: return@forEach
            if (hidden) qr.setAttribute("hidden", "") else qr.removeAttribute("hidden")
        }
    }

    // ==================== 시계 ====================

    private fun startClock() {
        tickClock()
        clockIntervalId = window.setInterval({ tickClock() }, 1000)
    }

    private fun stopClock() {
        clockIntervalId?.let { window.clearInterval(it) }
        clockIntervalId = null
    }

    private fun tickClock() {
        val clock = document.getElementById("kiosk-clock") as? HTMLElement ?: return
        clock.textContent = formatClock(Date())
    }

    private fun formatClock(date: Date): String {
        val hours24 = date.getHours()
        val period = if (hours24 < 12) "오전" else "오후"
        val hours12 = when {
            hours24 == 0 -> 12
            hours24 > 12 -> hours24 - 12
            else -> hours24
        }
        val minutes = date.getMinutes().toString().padStart(2, '0')
        val seconds = date.getSeconds().toString().padStart(2, '0')
        return "$period $hours12:$minutes:$seconds"
    }

    // ==================== Fullscreen · Wake Lock(dynamic — §5.4) ====================

    private fun requestFullscreen() {
        try {
            val root = document.documentElement.asDynamic() ?: return
            if (jsTypeOf(root.requestFullscreen) == "function") root.requestFullscreen()
        } catch (error: Throwable) {
            // 지원하지 않는 브라우저는 조용히 무시한다 — 전체화면 없이도 나머지 기능은 동작해야 한다.
        }
    }

    private fun exitFullscreenIfActive() {
        try {
            val doc = document.asDynamic()
            // dynamic과 Kotlin null 비교는 JS의 undefined도 함께 잡아준다(Kotlin/JS 상호운용 관례).
            if (doc.fullscreenElement != null && jsTypeOf(doc.exitFullscreen) == "function") {
                doc.exitFullscreen()
            }
        } catch (error: Throwable) {
            // no-op
        }
    }

    private fun requestWakeLock() {
        try {
            val nav = window.navigator.asDynamic()
            if (jsTypeOf(nav.wakeLock) != "object") {
                showWakeLockWarning()
                return
            }
            val promise = nav.wakeLock.request("screen") as? kotlin.js.Promise<dynamic>
            promise?.then({ sentinel: dynamic -> wakeLockSentinel = sentinel; Unit }, { _: dynamic -> showWakeLockWarning(); Unit })
        } catch (error: Throwable) {
            showWakeLockWarning()
        }
    }

    private fun releaseWakeLock() {
        try {
            wakeLockSentinel?.release()
        } catch (error: Throwable) {
            // no-op
        }
        wakeLockSentinel = null
    }

    private fun showWakeLockWarning() {
        (document.getElementById("wakelock-warning") as? HTMLElement)?.removeAttribute("hidden")
    }

    private fun ensureLifecycleHandlersWired() {
        if (lifecycleHandlersWired) return
        lifecycleHandlersWired = true
        window.addEventListener("beforeunload", { onBeforeUnload() })
    }

    /** 탭 종료·새로고침 시 best-effort로 세션을 닫는다 — `beforeunload`는 비동기 완료를 기다릴 수
     * 없으므로 await하지 않고 발사 후 잊는다(브라우저가 요청을 끝까지 보내지 못할 수도 있다). */
    private fun onBeforeUnload() {
        val current = activeSession
        if (current != null && current.status == QrSessionStatus.ACTIVE) {
            scope.launch { commandRepository.endQrSession(current.academyId, current.sessionId) }
        }
    }

    // ==================== 최근 스캔 · 이름 마스킹(§3.5.3) ====================

    private data class MaskedScan(val time: String, val maskedName: String, val label: String, val checkIn: Boolean)

    /**
     * 최근 스캔 스냅샷 1건만 가져온다(B5). `ObserveAttendanceFeedUseCase`가 감싼 Firestore
     * `onSnapshot` 기반 Flow는 실 서비스에서 완료되지 않는 무한 스트림이라 `.collect{}`로 기다리면
     * 이 suspend 함수가 영원히 반환하지 않는다 — `first()`가 첫 방출만 받고 구독을 즉시 취소한다
     * (`callbackFlow`의 `awaitClose { unsubscribe() }`가 그 취소 시점에 실행되어 리스너도 함께
     * 정리된다). 회전마다 다시 부르므로 "구독 1개 유지" 대신 "매회 열고 바로 닫기"를 택했다 —
     * 회전 주기(기본 20s)보다 훨씬 짧게 끝나는 단발성 조회라 비용 차이가 크지 않다.
     */
    private suspend fun recentScans(academyId: String): List<MaskedScan> {
        val events = try {
            withTimeoutOrNull(RECENT_SCANS_TIMEOUT_MS) {
                observeAttendanceFeed(academyId, 20).first()
            }
        } catch (error: CancellationException) {
            throw error
        } catch (error: Throwable) {
            // 스트림이 한 번도 방출하지 않고 닫히면(permission-denied 등) first()가 예외를 던진다.
            null
        } ?: emptyList()

        return events
            .filter { it.source == AttendanceSource.QR }
            .sortedByDescending { it.occurredAt }
            .take(5)
            .map {
                MaskedScan(
                    time = it.occurredAt.substringAfter('T').take(5),
                    maskedName = it.studentName.maskName(),
                    label = if (it.type.name == "CHECK_OUT") "하원" else "등원",
                    checkIn = it.type.name != "CHECK_OUT",
                )
            }
    }

    private suspend fun refreshRecentScans(academyId: String, generation: Int) {
        val recent = recentScans(academyId)
        if (!isCurrentRoute(generation)) return
        val list = document.getElementById("kiosk-recent") as? HTMLElement ?: return
        list.innerHTML = recent.joinToString("") { scan ->
            val modifier = if (scan.checkIn) "in" else "out"
            val iconId = if (scan.checkIn) "icon-check-in" else "icon-check-out"
            """
            <li class="eb-kiosk__recent-item eb-kiosk__recent-item--$modifier">
                ${icon(iconId, "md")}
                <span>${scan.maskedName.escapeHtml()} 학생 ${scan.label}</span><time>${scan.time.escapeHtml()}</time>
            </li>
            """.trimIndent()
        }
    }

    /** 가이드 §3.5.3 지정 유틸리티. 로비 공개 화면이므로 성 + 이름 첫 글자만 남긴다.
     * 빈 문자열이면 `first()`가 예외를 던지므로 먼저 걸러낸다. */
    private fun String.maskName(): String = when {
        isEmpty() -> ""
        length <= 2 -> "${first()}*"
        else -> "${take(2)}${"*".repeat(length - 2)}"
    }

    private const val MAX_QR_SESSION_LABEL_LENGTH = 60
    private const val RECENT_SCANS_TIMEOUT_MS = 3000L
}
